import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  Input,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { Sidebar, SidebarModule } from 'primeng/sidebar';
import { MovieSiteSideMenuColorSchemeConfiguration } from './movie-site-side-menu-color-scheme.model';

const ColorShemeVariableMap: Record<keyof Omit<MovieSiteSideMenuColorSchemeConfiguration, 'logoContrast'>, string> = {
  background: '--movie-site-side-menu-custom-background-color',
  text: '--movie-site-side-menu-custom-text-color',
  buttonBackground: '--movie-site-side-menu-custom-button-background-color',
  buttonText: '--movie-site-side-menu-custom-button-text-color',
  buttonHoverBackground: '--movie-site-side-menu-custom-button-hover-background-color'
};

let nextColorSchemeStyleId = 0;

/**
 */
@Component({
  selector: 'movie-site-side-menu',
  standalone: true,
  imports: [CommonModule, SidebarModule, FontAwesomeModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './movie-site-side-menu.component.html',
  styleUrl: './movie-site-side-menu.component.scss'
})
export class MovieSiteSideMenuComponent implements AfterViewInit {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly colorSchemeStyleId = `movie-site-side-menu-color-scheme-${nextColorSchemeStyleId++}`;
  private colorSchemeStyleElement?: HTMLStyleElement;
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @ViewChild('sidemenu') sidemenu!: Sidebar;
  @ViewChild('sidemenu', { read: ElementRef }) sidemenuHTML!: ElementRef<HTMLElement>;
  @ViewChild('overlay', { read: ElementRef }) overlay!: ElementRef<HTMLElement>;

  @Input() styleClass?: string;

  @Input() appendTo?: HTMLElement;

  @Input() linkClass = '';

  @Input() overlayZIndex = 5;

  @Input() closeIconFontSize?: string;

  @Input() closeIconColor?: string;

  readonly customColorScheme = input<MovieSiteSideMenuColorSchemeConfiguration>();

  readonly parsedColorSchemeVariables = computed<Record<string, string>>(() => {
    const styles: Record<string, string> = {};
    const customColorScheme = this.customColorScheme();
    if (!customColorScheme) return styles;
    const { background, text, buttonBackground, buttonText, buttonHoverBackground } = customColorScheme;
    styles[ColorShemeVariableMap.background] = background;
    styles[ColorShemeVariableMap.text] = text;
    styles[ColorShemeVariableMap.buttonBackground] = buttonBackground;
    styles[ColorShemeVariableMap.buttonText] = buttonText;
    styles[ColorShemeVariableMap.buttonHoverBackground] = buttonHoverBackground;
    return styles;
  });

  _sidebarVisible = false;
  get sidebarVisible(): boolean {
    return this._sidebarVisible;
  }
  set sidebarVisible(val: boolean) {
    const overlay = this.overlay.nativeElement;
    this._sidebarVisible = val;

    if (val) {
      overlay.style.display = 'block';
    } else {
      overlay.style.display = 'none';
      this.sidemenu.visible = false;
    }
  }

  readonly faMenu = faBars;

  constructor() {
    effect(() => this.updateColorSchemeStyle());
    this.destroyRef.onDestroy(() => this.removeColorSchemeStyle());
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.setCloseStyling();
    this.setSideOverlayElement();
  }

  setCloseStyling() {
    const el = this.sidemenuHTML.nativeElement;
    if (this.closeIconFontSize) el.style.setProperty('--close-icon__fontSize', this.closeIconFontSize);
    if (this.closeIconColor) el.style.setProperty('--close-icon__color', this.closeIconColor);
  }

  setSideOverlayElement() {
    if (this.appendTo) {
      const overlay = this.overlay.nativeElement;
      const appendTo = this.appendTo;
      appendTo.appendChild(overlay);
    }
  }

  private updateColorSchemeStyle(): void {
    if (!this.isBrowser) return;

    const colorSchemeVariables = Object.entries(this.parsedColorSchemeVariables());
    if (!colorSchemeVariables.length) {
      this.removeColorSchemeStyle();
      return;
    }

    if (!this.colorSchemeStyleElement) {
      const style = this.document.createElement('style');
      style.setAttribute('id', this.colorSchemeStyleId);
      this.document.head.appendChild(style);
      this.colorSchemeStyleElement = style;
    }

    let innerHTML = 'body {\n';
    for (const [key, value] of colorSchemeVariables) {
      innerHTML += `  ${key}: ${value};\n`;
    }
    innerHTML += '}';
    this.colorSchemeStyleElement.innerHTML = innerHTML;
  }

  private removeColorSchemeStyle(): void {
    this.colorSchemeStyleElement?.remove();
    this.colorSchemeStyleElement = undefined;
  }

  close() {
    this.sidebarVisible = false;
    this.sidemenu.visible = false;
  }
}
